import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './Preloader.css';

/**
 * Premium counter preloader: a 0 → 100 count-up with a solar progress
 * bar, then a two-panel "curtain" reveal that wipes off the screen.
 * Calls onDone() once the reveal completes so the app can play its intro.
 */
export default function Preloader({ onDone }) {
  const [count, setCount] = useState(0);
  const rootRef  = useRef(null);
  const barRef   = useRef(null);
  const numRef   = useRef(null);
  const wordRef  = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let finished = false;
    const finish = () => { if (finished) return; finished = true; onDone(); };

    // Safety net: if the tab is loaded in the background, requestAnimationFrame
    // (and thus GSAP) is throttled/paused and the intro could stall. Guarantee
    // the app is revealed within a hard ceiling regardless.
    const safety = setTimeout(finish, 9000);

    // Count 0 → 100 with easing (fast then settle)
    const counter = { v: 0 };
    const tl = gsap.timeline({
      onComplete: () => {
        // Curtain reveal
        const out = gsap.timeline({ onComplete: finish });
        out.to([numRef.current, wordRef.current, barRef.current?.parentNode], {
          y: -30, opacity: 0, duration: 0.5, ease: 'power3.in', stagger: 0.05,
        })
        .to('.pl-panel', {
          scaleY: 0, duration: 0.9, ease: 'power4.inOut', stagger: 0.08,
          transformOrigin: 'top',
        }, '-=0.1')
        .set(rootRef.current, { display: 'none' });
      },
    });

    tl.to(counter, {
      v: 100,
      duration: reduce ? 0.4 : 2.4,
      ease: 'power2.inOut',
      onUpdate: () => setCount(Math.round(counter.v)),
    });
    if (barRef.current) {
      tl.to(barRef.current, { scaleX: 1, duration: reduce ? 0.4 : 2.4, ease: 'power2.inOut' }, 0);
    }

    return () => { clearTimeout(safety); tl.kill(); };
  }, [onDone]);

  return (
    <div className="preloader" ref={rootRef}>
      <div className="pl-panel" />
      <div className="pl-panel" />
      <div className="pl-panel" />
      <div className="pl-panel" />

      <div className="pl-content">
        <div className="pl-word" ref={wordRef}>
          <span className="pl-mark" />
          C-Solar
        </div>

        <div className="pl-num" ref={numRef}>
          {String(count).padStart(3, '0')}<span className="pl-pct">%</span>
        </div>

        <div className="pl-bar-track">
          <div className="pl-bar" ref={barRef} />
        </div>

        <p className="pl-tag">Harnessing the sun</p>
      </div>
    </div>
  );
}
